<template>
  <div 
    class="tw-px-3 tw-py-2.5 hover:tw-bg-gray-50 tw-transition-colors tw-cursor-pointer"
    :class="{ 'tw-bg-primary-50 hover:tw-bg-primary-100': notification.unread}"
    @click="openNotificationDetails"
  >
    <div class="tw-flex tw-items-center tw-gap-2">
      <!-- Dot for unread -->
      <div v-if="notification.unread && false" class="tw-w-1.5 tw-h-1.5 tw-rounded-full" :class="getBgColor(notification.type)"></div>

      
      <!-- Icon -->
      <div class="tw-w-12 tw-h-12 tw-flex tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-white tw-border tw-border-solid tw-border-gray-200">
        <icon :icon="getIcon(notification.type)" :class="[getTextColor(notification.type), 'tw-text-xl']" />
      </div>
      
      <!-- Content -->
      <div class="tw-flex-1 tw-min-w-0">
        <div class="tw-flex tw-items-start tw-justify-between">
          <p class="tw-text-sm tw-font-medium tw-text-gray-800 tw-truncate">{{ notification.title }}</p>
          <span class="tw-text-xs tw-text-gray-400 tw-whitespace-nowrap tw-ml-1">{{ formatTime(notification.created_at || notification.time) }}</span>
        </div>
        <p class="tw-text-xs tw-text-gray-500 tw-mt-0.5 tw-line-clamp-1">{{ notification.message }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, ref, watch, computed } from 'vue';
import { useI18n } from "vue-i18n";
import useNotification from '@/services/notification';

const { t } = useI18n();
const notificationService = useNotification();

// State for losing products popup
const showLosingProductsPopup = ref(false);
const losingProducts = ref([]);

// State for low stock popup
const showLowStockPopup = ref(false);
const lowStockProducts = ref([]);

// State for stock recovered popup
const showStockRecoveredPopup = ref(false);
const stockRecoveredProducts = ref([]);

// State for order history popup
const showOrderHistoryPopup = ref(false);
const orderHistoryItem = ref(null);

// Computed property to check if any popup is open
const isAnyPopupOpen = computed(() => {
  return showLosingProductsPopup.value || 
         showLowStockPopup.value || 
         showStockRecoveredPopup.value || 
         showOrderHistoryPopup.value;
});

// Define props
const props = defineProps({
  notification: {
    type: Object,
    required: true
  }
});

// Define emits
const emit = defineEmits(['notificationRead', 'popupStateChanged']);

// Watch for changes in popup state and emit event
watch(isAnyPopupOpen, (newValue) => {
  emit('popupStateChanged', newValue);
});

// Open notification details and mark as read
function openNotificationDetails() {
  console.log(`DefaultNotification: openNotificationDetails called for notification ${props.notification.id}`);
  // Emit event to mark as read
  if (props.notification.unread) {
    console.log(`DefaultNotification: Emitting notificationRead for notification ${props.notification.id}`);
    emit('notificationRead', props.notification.id);
  } else {
    console.log(`DefaultNotification: Notification ${props.notification.id} is already read`);
  }
  
  // Handle different notification types (e.g., navigate to related pages)
  if (props.notification.data) {
    handleNotificationAction(props.notification);
  }
}

// Handle notification action based on type and data
function handleNotificationAction(notification) {
  try {
    // Check if notification is losing_products type
    if (notification.data.type === 'losing_products' && notification.data.products) {
      // Show the losing products popup
      losingProducts.value = notification.data.products;
      showLosingProductsPopup.value = true;
    } 
    // Check if notification is low_stock type
    else if (notification.data.type === 'low_stock' && notification.data.products) {
      // Show the low stock popup
      lowStockProducts.value = notification.data.products;
      showLowStockPopup.value = true;
    }
    // Check if notification is stock_recovered type
    else if (notification.data.type === 'stock_recovered' && notification.data.products) {
      // Show the stock recovered popup
      stockRecoveredProducts.value = notification.data.products;
      showStockRecoveredPopup.value = true;
    }
    // Check if notification is delivery_regression type
    else if (notification.data.type === 'delivery_regression' && notification.data.order) {
      // Show the order history popup directly
      orderHistoryItem.value = notification.data.order;
      showOrderHistoryPopup.value = true;
    }
    else {
      // Pass other notification types to the service's handleAction method
      notificationService.handleAction(notification);
    }
  } catch (error) {
    notificationService.handleError(error);
  }
}

// Helper functions for styling
const getBgColor = (type) => {
  switch(type) {
    case 'success': return 'tw-bg-emerald-500';
    case 'info': return 'tw-bg-blue-500';
    case 'danger': return 'tw-bg-red-500';
    case 'warning': return 'tw-bg-amber-500';
    default: return 'tw-bg-gray-500';
  }
};

const getTextColor = (type) => {
  switch(type) {
    case 'success': return 'tw-text-emerald-500';
    case 'info': return 'tw-text-blue-500';
    case 'danger': return 'tw-text-red-500';
    case 'warning': return 'tw-text-amber-500';
    default: return 'tw-text-gray-500';
  }
};

const getIcon = (type) => {
  switch(type) {
    case 'success': return 'solar:check-circle-bold';
    case 'info': return 'solar:info-circle-bold';
    case 'danger': return 'solar:close-circle-bold';
    case 'warning': return 'solar:bell-bold';
    default: return 'solar:info-circle-bold';
  }
};

// Format timestamp to relative time (e.g. "2h ago")
const formatTime = (timestamp) => {
  const now = new Date();
  let date;
  
  // Handle different timestamp formats
  if (typeof timestamp === 'string') {
    // ISO string format from API: "2025-04-26T23:35:51.000000Z"
    date = new Date(timestamp);
  } else if (typeof timestamp === 'number') {
    // Unix timestamp (seconds)
    date = new Date(timestamp * 1000);
  } else if (timestamp instanceof Date) {
    // Already a Date object
    date = timestamp;
  } else if (timestamp?.data?.timestamp) {
    // Handle notification.data.timestamp format
    date = new Date(timestamp.data.timestamp * 1000);
  } else {
    // Fallback to created_at if timestamp is undefined
    date = timestamp?.created_at ? new Date(timestamp.created_at) : new Date();
  }
  
  // Invalid date, use current time
  if (isNaN(date.getTime())) {
    date = new Date();
  }
  
  const diff = now - date;
  
  // Convert to minutes
  const minutes = Math.floor(diff / 60000);
  
  if (minutes < 60) {
    return minutes <= 0 
      ? t('time.just-now', 'Just now')
      : t('time.min-ago', { count: minutes }, `${minutes}m`);
  }
  
  // Convert to hours
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return t('time.hour-ago', { count: hours }, `${hours}h`);
  }
  
  // Convert to days
  const days = Math.floor(hours / 24);
  return t('time.day-ago', { count: days }, `${days}d`);
};
</script> 